import { FormattedMessage } from 'react-intl';

import { Button } from '@xing-com/button';
import type { Maybe } from '@xing-com/crate-common-graphql-types';

import { useCompanyData } from '../../hooks/use-company-data/use-company-data';
import { useFollowCompanyMutation } from '../../hooks/use-follow-company-mutation';
import { useUnfollowCompanyMutation } from '../../hooks/use-unfollow-company-mutation';
import {
  trackFollowCompany,
  trackUnfollowCompany,
  useNwtTrackingCompanies,
} from '../../utils/tracking';

const FollowButton = ({
  isFollowing,
  entityPageId,
  companyId,
  disabled,
  followText = 'CP_200_FOLLOW_LINK',
  className,
  position,
}: {
  isFollowing: boolean;
  entityPageId: string;
  companyId: Maybe<string>;
  followText?: string;
  className?: string;
  disabled: boolean;
  position: number;
}) => {
  const { handleFollow } = useFollowCompanyMutation({
    entityPageId,
  });
  const { handleUnfollow } = useUnfollowCompanyMutation({
    entityPageId,
  });
  const { globalCompaniesState } = useCompanyData();
  const pathname = window.location.pathname;
  const trackNwtEvent = useNwtTrackingCompanies();

  const resolveIsFollowing = () => {
    const company = globalCompaniesState.find(
      (item) => item?.entityPageId === entityPageId
    );

    if (!company?.userContext?.followState) return isFollowing;

    return company.userContext.followState.isFollowing;
  };

  const follow = ({ companyId }: { companyId: Maybe<string> }) => {
    trackFollowCompany(companyId);
    trackNwtEvent({
      eventSchema: 'basic',
      page: pathname,
      event: 'followed',
      itemUrn: `surn:x-xing:companies:company:${companyId}`,
      position,
    });
    handleFollow();
  };

  const unfollow = ({ companyId }: { companyId: Maybe<string> }) => {
    trackUnfollowCompany(companyId);
    trackNwtEvent({
      eventSchema: 'basic',
      page: pathname,
      event: 'unfollowed',
      itemUrn: `surn:x-xing:companies:company:${companyId}`,
      position,
    });
    handleUnfollow();
  };

  const resolvedIsFollowing = resolveIsFollowing();

  return (
    <Button
      size="small"
      variant="secondary"
      disabled={disabled}
      className={className}
      onClick={
        resolvedIsFollowing
          ? () => unfollow({ companyId })
          : () => follow({ companyId })
      }
    >
      <FormattedMessage
        id={resolvedIsFollowing ? 'CP_200_UNFOLLOW_LINK' : followText}
      />
    </Button>
  );
};

export { FollowButton };
